import { Injectable, inject } from '@angular/core';
import { FibiiWebservice, Options } from '@wizbii-utils/angular/webservices';
import {
  AppointmentStatusEnum,
  ContactModeEnum,
  DossierWithClient,
  ExportPhoneNumbersResponse,
  HelpsTypeFilter,
  Order,
  OrderStatusEnum,
  OrdersResponse,
} from '@wizbii/utils/models';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HelpiiWebservice {
  private readonly fibii = inject(FibiiWebservice);

  getAllOrders(
    param: {
      nbItemsPerPage: number;
      offset: number;
      ownerIds: string[];
      country: string;
      partnerIds?: string[];
      lastAppointmentStatus?: AppointmentStatusEnum | 'default';
      appointmentPlannedDateFrom?: string;
      appointmentPlannedDateTo?: string;
      helpsType?: HelpsTypeFilter;
      appointmentPlanned?: boolean;
      acquaintanceMailSent?: boolean;
      additionalFormStatus?: string[];
      sortDate?: boolean;
      orderCreationDateFrom?: string;
      orderCreationDateTo?: string;
      contactMode?: ContactModeEnum;
      status?: OrderStatusEnum;
    },
    options?: Options
  ): Observable<OrdersResponse> {
    return this.fibii.getAllOrders(
      {
        rows: param.nbItemsPerPage,
        offset: param.offset,
        ownerIds: param.ownerIds ?? [],
        country: param.country,
        ...(param.partnerIds ? { partnerIds: param.partnerIds } : undefined),
        ...(param.lastAppointmentStatus ? { lastAppointmentStatus: param.lastAppointmentStatus } : undefined),
        ...(param.appointmentPlannedDateFrom
          ? { appointmentPlannedDateFrom: param.appointmentPlannedDateFrom }
          : undefined),
        ...(param.appointmentPlannedDateTo ? { appointmentPlannedDateTo: param.appointmentPlannedDateTo } : undefined),
        ...(param.helpsType ? { helpsType: param.helpsType } : undefined),
        ...(param.appointmentPlanned ? { appointmentPlanned: param.appointmentPlanned } : {}),
        ...(param.acquaintanceMailSent ? { acquaintanceMailSent: param.acquaintanceMailSent } : {}),
        ...(param.additionalFormStatus ? { additionalFormStatus: param.additionalFormStatus } : {}),
        ...(param.orderCreationDateFrom ? { orderCreationDateFrom: param.orderCreationDateFrom } : undefined),
        ...(param.orderCreationDateTo ? { orderCreationDateTo: param.orderCreationDateTo } : undefined),
        ...(param.contactMode ? { contactMode: param.contactMode } : undefined),
        ...(param.status ? { status: param.status } : undefined),
        ...(param.sortDate === true || param.sortDate === false ? { sortDate: param.sortDate } : undefined),
      },
      options
    );
  }

  getPhoneNumber(
    param: {
      nbItemsPerPage: number;
      ownerIds: string[];
      country: string;
      partnerIds?: string[];
      lastAppointmentStatus?: AppointmentStatusEnum | 'default';
      appointmentPlannedDateFrom?: string;
      appointmentPlannedDateTo?: string;
      helpsType?: HelpsTypeFilter;
      appointmentPlanned?: boolean;
      acquaintanceMailSent?: boolean;
      additionalFormStatus?: string[];
      sortDate?: boolean;
      orderCreationDateFrom?: string;
      orderCreationDateTo?: string;
      contactMode?: ContactModeEnum;
      status?: OrderStatusEnum;
    },
    options?: Options
  ): Observable<ExportPhoneNumbersResponse> {
    return this.fibii.getPhoneNumberCsv(
      {
        ownerIds: param.ownerIds ?? [],
        country: param.country,
        ...(param.partnerIds ? { partnerIds: param.partnerIds } : undefined),
        ...(param.lastAppointmentStatus ? { lastAppointmentStatus: param.lastAppointmentStatus } : undefined),
        ...(param.appointmentPlannedDateFrom
          ? { appointmentPlannedDateFrom: param.appointmentPlannedDateFrom }
          : undefined),
        ...(param.appointmentPlannedDateTo ? { appointmentPlannedDateTo: param.appointmentPlannedDateTo } : undefined),
        ...(param.helpsType ? { helpsType: param.helpsType } : undefined),
        ...(param.appointmentPlanned ? { appointmentPlanned: param.appointmentPlanned } : {}),
        ...(param.acquaintanceMailSent ? { acquaintanceMailSent: param.acquaintanceMailSent } : {}),
        ...(param.additionalFormStatus ? { additionalFormStatus: param.additionalFormStatus } : {}),
        ...(param.orderCreationDateFrom ? { orderCreationDateFrom: param.orderCreationDateFrom } : undefined),
        ...(param.orderCreationDateTo ? { orderCreationDateTo: param.orderCreationDateTo } : undefined),
        ...(param.contactMode ? { contactMode: param.contactMode } : undefined),
        ...(param.status ? { status: param.status } : undefined),
        ...(param.sortDate === true || param.sortDate === false ? { sortDate: param.sortDate } : undefined),
      },
      options
    );
  }

  getDossiersFromIds(dossierIds: string[], options?: Options): Observable<DossierWithClient[]> {
    const fetchDossierChunk = (ids: string[], options?: Options) =>
      this.fibii.getDossiersFromIds(ids.join(','), options);

    if (dossierIds.length <= 100) {
      return fetchDossierChunk(dossierIds, options).pipe(catchError(() => []));
    }

    const chunkedDossiers = dossierIds.reduce((resultArray: string[][], item, index) => {
      const chunkSize = 100;
      const chunkIndex = Math.floor(index / chunkSize);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex] = [...resultArray[chunkIndex], item];

      return resultArray;
    }, []);

    return forkJoin(
      chunkedDossiers.map((ids) => {
        return fetchDossierChunk(ids, options).pipe(catchError(() => []));
      })
    ).pipe(
      map((chunkedDossiers) => {
        return chunkedDossiers.reduce((acc, chunk) => [...acc, ...chunk], []);
      })
    );
  }

  getOrdersFromIds(ordersIds: string[], options?: Options): Observable<Order[]> {
    if (ordersIds.length === 0) return of([]);
    const fetchDossierChunk = (ids: string[], options?: Options) => this.fibii.getOrdersFromIds(ids.join(','), options);

    if (ordersIds.length <= 100) {
      return fetchDossierChunk(ordersIds, options).pipe(catchError(() => []));
    }

    const chunkedOrders = ordersIds.reduce((resultArray: string[][], item, index) => {
      const chunkSize = 100;
      const chunkIndex = Math.floor(index / chunkSize);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex] = [...resultArray[chunkIndex], item];

      return resultArray;
    }, []);
    return forkJoin(chunkedOrders.map((ids) => fetchDossierChunk(ids, options).pipe(catchError(() => [])))).pipe(
      map((orders) => orders.reduce((acc, chunk) => [...acc, ...chunk], []))
    );
  }
}
